import { escapeField, downloadCsv } from 'screens/Main/tabs/Send/utils/csvExport';
import { getLanguageDisplayName } from 'constants/languages';
import { FINISH_LABELS, finishFromRow, HaveFinish, wantAcceptedFinishes } from 'utils/finishes';

// --- Have List ---

export interface HaveListExportItem {
  quantity: number;
  cardName: string;
  setName: string;
  setCode: string;
  collectorNumber: string | null;
  condition: string;
  language: string;
  foil: boolean;
  /** Track D; derived from foil when absent */
  finish?: HaveFinish | null;
  forTrade: boolean;
  pointValue: number | null;
  scryfallId: string;
}

export interface HaveListExportResponse {
  items: HaveListExportItem[];
  truncated: boolean;
}

// The importer maps the Scryfall ID columns by header name ("Scryfall ID" /
// "Scryfall IDs") - keep these header strings exact.
const HAVE_LIST_CSV_HEADER = 'Quantity,Card Name,Set Name,Set Code,Collector Number,Finish,Condition,Language,Value,For Trade,Scryfall ID';

const CONDITION_ABBREVIATIONS: Record<string, string> = {
  NEAR_MINT: 'NM',
  SLIGHTLY_PLAYED: 'LP',
  MODERATELY_PLAYED: 'MP',
  HEAVILY_PLAYED: 'HP',
  DAMAGED: 'DMG',
};

function abbreviateCondition(condition: string): string {
  return CONDITION_ABBREVIATIONS[condition] || condition || '';
}

export function generateHaveListCsv(items: HaveListExportItem[]): string {
  const lines = items.map(item =>
    [
      String(item.quantity),
      escapeField(item.cardName),
      escapeField(item.setName || ''),
      escapeField((item.setCode || '').toUpperCase()),
      item.collectorNumber || '',
      exportFinishLabel(finishFromRow(item)),
      abbreviateCondition(item.condition),
      getLanguageDisplayName(item.language || 'en'),
      item.pointValue != null ? String(item.pointValue) : '???',
      item.forTrade ? 'Yes' : 'No',
      item.scryfallId || '', // single UUID, comma-free - no escaping needed
    ].join(',')
  );
  return [HAVE_LIST_CSV_HEADER, ...lines].join('\n');
}

// --- Want List ---

export interface WantListExportItem {
  quantity: number;
  cardName: string;
  acceptableSets: string[];
  acceptableConditions: string[];
  acceptedLanguages: string[];
  collectorNumbers: string | null;
  acceptFoil: boolean;
  acceptNonFoil: boolean;
  acceptEtched?: boolean | null;
  pointValue: number | null;
  scryfallIds: string | null; // ALL accepted printing UUIDs, comma-joined
}

export interface WantListExportResponse {
  items: WantListExportItem[];
  truncated: boolean;
}

const WANT_LIST_CSV_HEADER = 'Quantity,Card Name,Sets,Collector Number,Finish,Conditions,Languages,Value,Scryfall IDs';

// The importer's Finish vocabulary: Foil, Nonfoil, Etched (exact words)
const exportFinishLabel = (f: 'nonfoil' | 'foil' | 'etched'): string =>
  f === 'nonfoil' ? 'Nonfoil' : FINISH_LABELS[f];

function buildFinish(item: Pick<WantListExportItem, 'acceptFoil' | 'acceptNonFoil' | 'acceptEtched'>): string {
  return wantAcceptedFinishes(item).map(exportFinishLabel).join(', ');
}

function buildConditions(conditions: string[]): string {
  if (!conditions || conditions.length === 0) return 'Any';
  return conditions.map(c => abbreviateCondition(c)).join(', ');
}

function buildLanguages(languages: string[]): string {
  if (!languages || languages.length === 0) return 'Any';
  return languages.map(l => getLanguageDisplayName(l)).join(', ');
}

export function generateWantListCsv(items: WantListExportItem[]): string {
  const lines = items.map(item =>
    [
      String(item.quantity),
      escapeField(item.cardName),
      escapeField(item.acceptableSets?.length > 0 ? item.acceptableSets.join(', ') : 'Any'),
      escapeField(item.collectorNumbers || ''),
      escapeField(buildFinish(item)),
      escapeField(buildConditions(item.acceptableConditions)),
      escapeField(buildLanguages(item.acceptedLanguages)),
      item.pointValue != null ? String(item.pointValue) : '???',
      // Comma-joined UUID list - MUST go through escapeField for quoting
      escapeField(item.scryfallIds || ''),
    ].join(',')
  );
  return [WANT_LIST_CSV_HEADER, ...lines].join('\n');
}

// Re-export downloadCsv for convenience
export { downloadCsv };
